import {
  AddressWithType,
  Contact,
  Good,
  WebAccess,
} from '@/api/freedomQl/__generated__/graphql';
import {
  AllowedMop,
  BookingContact,
  BookingFlow,
  BookingQuote,
  BookingStop,
  BookingTemplate,
  ErrorResponse,
  IntegraAccountSettings,
  IntegraProfile,
  Tariff,
  Voucher,
} from '@/api/integraLink/types';
import {
  BookingType,
  Country,
  GoodsType,
  ThemePreference,
} from '@/config/constants';

import { AuthToken } from '@/api/authProxy/types';
import { LocationPermissionState } from '@/utils/permissions';
import { Position } from '@capacitor/geolocation';
import config from '@/config';
import { toMinute } from '@/utils/dateManipulation';

type Parameter = {
  code: string;
  setting: string;
};

type SignInDetails = {
  rememberedEmail?: string;
  confirmSignInPending: boolean;
  defaults: {
    name?: string;
    email?: string;
    mobile?: string;
    country?: Country;
  };
};

type MFADetails = {
  wrongPassCount: number;
  pendingSMS: boolean;
  pendingTOTP: boolean;
  email: string;
  totpSetupDetails?: {
    sharedSecret: string;
    getSetupUri(appName: string, accountName?: string): URL;
  };
};

type ActiveBooking = {
  bookingType?: BookingType;
  fromAddress?: AddressWithType;
  toAddress?: AddressWithType;
  bookingForm?: BookingFormState;
  bookingTemplate?: BookingTemplate;
  earliestPickup?: string;
  earliestArrival?: string;
  tariffs?: Tariff[];
  goodsTypes?: GoodsType[];
  mops?: AllowedMop[];
  quotes: BookingQuote[];
  quoteError?: ErrorResponse;
  journeyDistance?: number;
  journeyDurationMins?: number;
  setValues?: { dataName: string; value: any }[];
  ready: boolean;
  copyBookingRef?: number;
  returnBookingRef?: number;
  currentDeviceMinute: number;
  defaultDelay: number;
};

export type BookingFormState = {
  asap?: boolean;
  arriveBy?: boolean;
  requiredDateTime?: string;
  contact?: BookingContact;
  despatchInstructions?: string;
  goodsQty?: number;
  goodsCarton?: number;
  tariff?: string;
  mop?: string;
  goods?: Good[];
  stops: BookingStop[];
  vouchers?: Voucher[];
  // all other values (jobFlags / references / jobExtras)
  [key: string]: any;
  jobQuoteRef?: number;
};

export type MasqueradeDetails = {
  count: number;
  useSearch: boolean;
  contacts: Contact[];
};

export type AppDetails = {
  updated?: string;
  bookingFlow?: BookingFlow;
  webAccess?: WebAccess;
  bookFor?: MasqueradeDetails;
  masqueradeAndMaintain?: MasqueradeDetails;
};

type ContactWithAccountSettings = Contact & {
  accountWebSettings?: IntegraAccountSettings;
};

export type State = {
  locale: string;
  themePreference: ThemePreference;
  location: {
    position?: Position;
    permissions: LocationPermissionState;
  };
  signInDetails: SignInDetails;
  mfaDetails: MFADetails;
  integraLinkJwt: string;
  appDetails: AppDetails;
  integraProfile: IntegraProfile;
  isGuestSession: boolean;
  activeBooking: ActiveBooking;
  appData: {
    parameters: Parameter[];
  };
  profileContact: ContactWithAccountSettings;
  jobContact: ContactWithAccountSettings;
  cookieConsent: boolean;
  quickQuote?: {
    formValues: BookingFormState;
    goodsType: GoodsType;
  };
  ssoBooking?: {
    templateId?: string;
    quoteId?: string;
  };
  authProxyToken?: AuthToken;
  // Whether guests can get a quote, kept from the last lookup that succeeded
  quickQuoteAvailable?: boolean;
  websocketStatus: 'connected' | 'disconnected' | 'reconnecting';
  pushRegistrationToken?: string;
  pushEndpointArn?: string;
};

export const getDefaultActiveBooking = () => {
  return {
    fromAddress: undefined,
    toAddress: undefined,
    bookingForm: undefined,
    bookingTemplate: undefined,
    earliestPickup: undefined,
    earliestArrival: undefined,
    tariffs: undefined,
    goodsTypes: undefined,
    mops: undefined,
    quotes: [] as BookingQuote[],
    quoteError: undefined,
    journeyDistance: undefined,
    journeyDurationMins: undefined,
    setValues: undefined,
    ready: false,
    currentDeviceMinute: toMinute(new Date()).getTime(),
    defaultDelay: config.app.bookingForm.fallbackDefaultDelayMins,
  };
};

export const getDefaultAppDetails = () => {
  return {
    updated: undefined,
    bookingFlow: undefined,
    bookFor: {
      count: 0,
      useSearch: false,
      contacts: [],
    },
    masqueradeAndMaintain: {
      count: 0,
      useSearch: false,
      contacts: [],
    },
  };
};

export const getDefaultSignInDetails = () => ({
  confirmSignInPending: false,
  defaults: {},
});

export const getDefaultMFADetails = () => ({
  wrongPassCount: 0,
  pendingSMS: false,
  pendingTOTP: false,
  email: '',
});

export const getDefaultState = (): State => {
  return {
    locale: config.app.defaultLocale,
    themePreference: ThemePreference.LIGHT,
    location: {
      permissions: 'unknown',
    },
    signInDetails: getDefaultSignInDetails(),
    mfaDetails: getDefaultMFADetails(),
    integraLinkJwt: '',
    appDetails: getDefaultAppDetails(),
    integraProfile: {} as IntegraProfile,
    isGuestSession: false,
    activeBooking: getDefaultActiveBooking(),
    appData: {
      parameters: [] as Parameter[],
    },
    profileContact: {} as ContactWithAccountSettings,
    jobContact: {} as ContactWithAccountSettings,
    cookieConsent: false,
    quickQuote: undefined,
    ssoBooking: undefined,
    authProxyToken: undefined,
    quickQuoteAvailable: undefined,
    websocketStatus: 'disconnected',
    pushRegistrationToken: undefined,
    pushEndpointArn: undefined,
  };
};
